import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageHeader, SectionHeading, Reveal } from "../components/ui";

const workstreams = [
  {
    workstream: "Workforce blueprint",
    scope: "Role priorities, capability gaps, seniority, sequencing, location and full-time/contract/managed-team mix.",
    outcome: "A hiring plan tied to roadmap and runway.",
  },
  {
    workstream: "Team build",
    scope: "Sourcing, structured screening, interview coordination, selection support and offer-process coordination.",
    outcome: "Less noise and fewer founder hours per hire.",
  },
  {
    workstream: "Workforce activation",
    scope: "Documentation and onboarding coordination, access checklist, induction plan and first-30-day cadence.",
    outcome: "A cleaner start and faster productivity.",
  },
  {
    workstream: "Ongoing operations",
    scope: "Agreed contractor/vendor coordination, workforce reviews, backfill support and issue escalation.",
    outcome: "One operating interface instead of fragmented vendors.",
  },
  {
    workstream: "Scale and change",
    scope: "Ramp-up, ramp-down, role changes and transition planning within the approved engagement model.",
    outcome: "Capacity can follow the business without unnecessary fixed overhead.",
  },
];

const engagementModels = [
  {
    model: "Managed hiring",
    bestWhen: "The startup will employ the team but needs an external hiring engine.",
    means: "Chiparama manages the agreed search, screening and hiring workflow; the client remains the employer.",
  },
  {
    model: "Flexible workforce",
    bestWhen: "Capacity is needed without adding every role to permanent payroll.",
    means: "Specialists are engaged through an approved contract structure that is validated for the role and geography.",
  },
  {
    model: "Managed team / pod",
    bestWhen: "The startup needs a defined capability or workstream, not isolated profiles.",
    means: "A scoped team operates with agreed leadership, cadence and deliverables.",
  },
  {
    model: "Build-Operate-Transfer",
    bestWhen: "The startup wants to create an internal capability over time.",
    means: "Chiparama builds and stabilizes the team, then transfers it against an agreed plan and conditions.",
  },
];

const howItWorks = [
  { step: "Align", description: "Map the business milestone, product roadmap, runway, current team and hiring constraints." },
  { step: "Design", description: "Create the workforce blueprint, role sequence, engagement mix and accountability matrix." },
  { step: "Build", description: "Source, assess and coordinate selection through a role-appropriate process." },
  { step: "Activate", description: "Complete the approved documentation and onboarding workflow, then launch the operating cadence." },
  { step: "Operate", description: "Review workforce health, resolve gaps, manage agreed changes and scale capacity as the business evolves." },
];

const whyFounders = [
  "One accountable partner across the workforce journey instead of disconnected agencies and vendors.",
  "Specialist screening informed by the work, not only the job description.",
  "Flexible models that can move from one critical hire to a managed team or BOT.",
  "A deliberate operating cadence that protects founder time while keeping decisions visible.",
];

const DESCRIPTION =
  "Build and manage a tech startup workforce with Chiparama - from role planning and specialist hiring to onboarding coordination and flexible team operations.";

export default function StartupWorkforcePage() {
  return (
    <>
      <Helmet>
        <title>Startup Workforce | Chiparama</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://chiparama.com/startup-workforce" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Startup Workforce | Chiparama" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://chiparama.com/startup-workforce" />
        <meta property="og:image" content="https://chiparama.com/chiparamalogo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Startup Workforce | Chiparama" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://chiparama.com/chiparamalogo.jpg" />
      </Helmet>

      <PageHeader
        eyebrow="Startup Workforce. One accountable partner."
        title="Build the company without building a full workforce function first."
        actions={
          <>
            <Link to="/contact" className="btn-primary px-7 py-3.5">Plan my workforce</Link>
            <a href="#engagement-models" className="btn-outline-dark px-7 py-3.5">Explore engagement models</a>
          </>
        }
      >
        From workforce planning and role design to sourcing, screening, onboarding coordination and
        flexible team operations, Chiparama helps tech startups build the team they need while keeping
        fixed overhead under control.
      </PageHeader>

      <section className="section bg-white">
        <div className="container-x max-w-4xl">
          <h2 className="h-section">Your next ten hires should not become the founders' second full-time job.</h2>
          <p className="lede mt-6">
            Every early-stage hire competes for founder attention. Role design, sourcing, screening,
            interview coordination, onboarding and workforce administration quickly become a fragmented
            operating layer. Chiparama gives the company one plan, one accountable interface and the
            flexibility to add capability without building permanent infrastructure before it is needed.
          </p>
        </div>
      </section>

      <section className="section bg-paper">
        <div className="container-x">
          <SectionHeading eyebrow="Scope" title="What Chiparama handles" />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-6">
            {workstreams.map((item, i) => (
              <Reveal key={item.workstream} delay={(i % 3) * 80} className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}>
                <div className="card card-hover h-full !rounded-3xl !p-8">
                  <span className="font-mono text-xs text-brand">0{i + 1}</span>
                  <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-ink">{item.workstream}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.scope}</p>
                  <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-tint px-3.5 py-1.5 text-sm font-medium text-brand">
                    <span className="h-1.5 w-1.5 rounded-full bg-signal" aria-hidden="true" />
                    {item.outcome}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="engagement-models" className="on-dark section scroll-mt-20 bg-navy-900 text-white">
        <div className="container-x">
          <SectionHeading eyebrow="Engagement models" title="The right structure for the stage." dark />
          <div className="grid gap-5 md:grid-cols-2">
            {engagementModels.map((item) => (
              <div key={item.model} className="card-dark">
                <h3 className="text-xl font-semibold text-white">{item.model}</h3>
                <p className="mt-3 text-sm font-medium text-brand-light">Best when: {item.bestWhen}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">{item.means}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="Process" title="How it works" />
          <ol className="space-y-8">
            {howItWorks.map((item, index) => (
              <li key={item.step} className="flex gap-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line font-mono text-sm text-brand">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-ink">{item.step}</h3>
                  <p className="mt-1.5 leading-relaxed text-slate-600">{item.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section bg-paper">
        <div className="container-x">
          <SectionHeading eyebrow="Why Chiparama" title="Why founders choose this" />
          <ul className="grid gap-5 md:grid-cols-2">
            {whyFounders.map((point) => (
              <li key={point} className="card flex gap-4 text-slate-700">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
                {point}
              </li>
            ))}
          </ul>
          <p className="mt-10 text-sm text-slate-500">
            Service scope and employment model vary by role and geography.
          </p>
        </div>
      </section>

      <section className="on-dark section bg-navy-950 text-white">
        <div className="container-x max-w-3xl text-center">
          <h2 className="h-section">Build the workforce your next milestone needs.</h2>
          <p className="lede mt-6">
            Tell us what you are building, which roles are becoming urgent and how much internal hiring
            capacity you have today. We will recommend the right workforce model and the first practical
            step.
          </p>
          <Link to="/contact" className="btn-primary mt-10 px-8 py-3.5">Plan my startup workforce</Link>
        </div>
      </section>
    </>
  );
}
